import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  CheckCircle2, XCircle, Loader2, MapPin, Calendar, ImageIcon, ChevronDown, ChevronRight, RefreshCw,
} from "lucide-react";
import { cloudinaryThumb } from "@/lib/cloudinary";
import { toast } from "sonner";
import PushNotifSettings from "@/components/PushNotifSettings";

const CIERRE_LABEL = {
  montado: { label: "Montado", color: "bg-emerald-500/20 text-emerald-300" },
  semi_montado: { label: "Semi", color: "bg-amber-500/20 text-amber-300" },
  incidencia: { label: "Incidencia", color: "bg-red-500/20 text-red-300" },
  cancelado: { label: "Cancel.", color: "bg-zinc-700/40 text-zinc-300" },
};

function fechaCorta(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("es-ES", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso.slice(0, 10); }
}

export default function FotosPendientes() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyFoto, setBusyFoto] = useState(null);
  const [openIds, setOpenIds] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/fotos-pendientes-cliente?dias=21");
      setData(d || []);
      // Auto-open todas las primeras 3
      const ids = {};
      (d || []).slice(0, 3).forEach((r) => { ids[`${r.ruta_id}-${r.idx}`] = true; });
      setOpenIds(ids);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error cargando");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const aprobarFoto = async (reporte, foto, aprobada) => {
    setBusyFoto(foto.id);
    try {
      await api.post(
        `/rutas/${reporte.ruta_id}/paradas/${reporte.idx}/fotos/${foto.id}/aprobacion-cliente`,
        { aprobada }
      );
      // Quitar la foto del listado localmente (ya aprobada o rechazada explícitamente)
      setData((cur) => cur.map((r) => {
        if (r.ruta_id !== reporte.ruta_id || r.idx !== reporte.idx) return r;
        const restantes = r.fotos_pendientes.filter((f) => f.id !== foto.id);
        return { ...r, fotos_pendientes: restantes, n_pendientes: restantes.length };
      }).filter((r) => r.fotos_pendientes.length > 0));
      toast.success(aprobada ? "✓ Aprobada para cliente" : "✗ Rechazada (no se envía)");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    } finally { setBusyFoto(null); }
  };

  const aprobarTodasReporte = async (reporte) => {
    try {
      await api.post(`/rutas/${reporte.ruta_id}/paradas/${reporte.idx}/fotos/aprobar-todas`);
      toast.success(`✓ ${reporte.fotos_pendientes.length} foto(s) aprobadas`);
      setData((cur) => cur.filter((r) => !(r.ruta_id === reporte.ruta_id && r.idx === reporte.idx)));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  const totalFotos = data.reduce((acc, r) => acc + r.n_pendientes, 0);
  const totalReportes = data.length;

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto pb-24">
      {/* Header mobile-first */}
      <div className="sticky top-0 z-10 -mx-4 sm:-mx-6 px-4 sm:px-6 py-3 bg-zinc-950/95 backdrop-blur border-b border-zinc-800 mb-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="text-lg sm:text-xl font-bold flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-amber-400" />
              Fotos pendientes
            </h1>
            <div className="text-[11px] sm:text-xs text-zinc-400 mt-0.5" data-testid="fotos-pend-resumen">
              {loading ? "Cargando…" : (
                totalFotos === 0
                  ? "✓ Todo al día"
                  : `${totalFotos} fotos en ${totalReportes} reporte${totalReportes !== 1 ? "s" : ""}`
              )}
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <PushNotifSettings compact />
            <button
              onClick={load}
              disabled={loading}
              data-testid="refresh-fotos-pend"
              className="p-2 rounded-lg bg-zinc-900 border border-zinc-800 text-zinc-300 active:bg-zinc-800"
              aria-label="Refrescar"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16 text-zinc-500">
          <Loader2 className="w-6 h-6 animate-spin inline" />
        </div>
      ) : data.length === 0 ? (
        <div className="text-center py-16">
          <CheckCircle2 className="w-12 h-12 text-emerald-500/40 mx-auto mb-3" />
          <div className="text-zinc-300 font-semibold">Sin fotos pendientes</div>
          <div className="text-xs text-zinc-500 mt-1">Todo revisado en los últimos 21 días.</div>
        </div>
      ) : (
        <div className="space-y-3" data-testid="fotos-pend-lista">
          {data.map((r) => {
            const key = `${r.ruta_id}-${r.idx}`;
            const isOpen = openIds[key];
            const lab = CIERRE_LABEL[r.estado_cierre] || CIERRE_LABEL.montado;
            return (
              <div key={key} data-testid={`reporte-pend-${r.ruta_codigo}-${r.idx}`}
                className="bg-zinc-900/60 border border-zinc-800 rounded-xl overflow-hidden">
                {/* Cabecera reporte (tap para expandir) */}
                <button
                  onClick={() => setOpenIds((o) => ({ ...o, [key]: !o[key] }))}
                  className="w-full text-left p-3 flex items-start gap-2 active:bg-zinc-800/50">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-[10px] font-mono text-amber-400">{r.ruta_codigo}</span>
                      <span className="text-[10px] text-zinc-500">#{r.parada_orden}</span>
                      <span className={`text-[9px] uppercase px-1.5 py-0.5 rounded ${lab.color}`}>{lab.label}</span>
                      <span className="ml-auto bg-amber-500/20 text-amber-200 text-[10px] font-bold px-2 py-0.5 rounded-full">
                        {r.n_pendientes} pendiente{r.n_pendientes !== 1 ? "s" : ""}
                      </span>
                    </div>
                    <div className="text-sm font-bold truncate">{r.espacio_nombre || "—"}</div>
                    <div className="text-[11px] text-zinc-400 truncate">
                      {r.cliente_nombre} · {r.peticion_codigo}
                    </div>
                    <div className="text-[10px] text-zinc-500 mt-0.5 flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center gap-1">
                        <Calendar className="w-2.5 h-2.5" /> {fechaCorta(r.finalizado_at)}
                      </span>
                      {r.espacio_ciudad && (
                        <span className="inline-flex items-center gap-1">
                          <MapPin className="w-2.5 h-2.5" /> {r.espacio_ciudad}
                        </span>
                      )}
                      <span className="text-zinc-600">· por {r.actualizado_por || "—"}</span>
                    </div>
                  </div>
                  {isOpen ? <ChevronDown className="w-4 h-4 text-zinc-500 mt-1" /> : <ChevronRight className="w-4 h-4 text-zinc-500 mt-1" />}
                </button>

                {/* Fotos expandidas */}
                {isOpen && (
                  <div className="p-3 pt-0 border-t border-zinc-800">
                    <div className="flex items-center justify-between mb-2 mt-2">
                      <Link
                        to={`/reportes`}
                        className="text-[11px] text-cyan-400 underline">
                        Ver reporte completo →
                      </Link>
                      <Button
                        size="sm"
                        onClick={() => aprobarTodasReporte(r)}
                        data-testid={`aprobar-todas-${r.ruta_codigo}-${r.idx}`}
                        className="h-7 text-[11px] bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-200 border border-emerald-500/30">
                        ✓ Aprobar todas ({r.n_pendientes})
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {r.fotos_pendientes.map((f) => {
                        const busy = busyFoto === f.id;
                        return (
                          <div key={f.id} className="bg-zinc-950 border border-zinc-800 rounded-lg overflow-hidden" data-testid={`foto-pend-${f.id}`}>
                            <a href={f.url} target="_blank" rel="noreferrer" className="block relative">
                              <img src={cloudinaryThumb(f.url, 400, 300)} alt={f.categoria}
                                className="w-full h-32 sm:h-40 object-cover" />
                              <div className="absolute top-1 left-1 bg-black/75 rounded px-1.5 py-0.5 text-[10px] uppercase">
                                {f.categoria}
                              </div>
                              {f.latitud && (
                                <div className="absolute bottom-1 left-1 bg-emerald-500/80 text-zinc-900 text-[9px] px-1 rounded font-semibold">
                                  📍 GPS
                                </div>
                              )}
                            </a>
                            <div className="grid grid-cols-2 gap-px bg-zinc-800">
                              <button
                                disabled={busy}
                                onClick={() => aprobarFoto(r, f, false)}
                                data-testid={`rechazar-foto-${f.id}`}
                                className="bg-zinc-900 hover:bg-red-500/20 active:bg-red-500/30 text-red-300 py-2.5 text-xs font-semibold disabled:opacity-50 flex items-center justify-center gap-1">
                                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />}
                                Rechazar
                              </button>
                              <button
                                disabled={busy}
                                onClick={() => aprobarFoto(r, f, true)}
                                data-testid={`aprobar-foto-${f.id}`}
                                className="bg-emerald-500 hover:bg-emerald-400 active:bg-emerald-600 text-zinc-900 py-2.5 text-xs font-bold disabled:opacity-50 flex items-center justify-center gap-1">
                                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                                Aprobar
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Tip footer mobile */}
      {!loading && data.length > 0 && (
        <div className="fixed bottom-4 left-4 right-4 sm:hidden bg-zinc-900/95 backdrop-blur border border-zinc-800 rounded-lg p-2.5 text-[10px] text-zinc-400 shadow-lg">
          💡 Estás validando desde el móvil. Solo las fotos <b className="text-emerald-400">aprobadas</b> se envían al cliente o quedan en la galería pública.
        </div>
      )}
    </div>
  );
}
